import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaUsers, FaCoins, FaUser, FaGlobe, FaLock, FaExclamationCircle } from 'react-icons/fa';
import { apiService } from '../services/apiService';
import { toast } from 'react-toastify';

interface CreateChallengeModalProps {
  game: {
    id: number;
    name: string;
    type: string;
    minStake: number;
    maxStake: number;
    description: string;
  };
  onClose: () => void;
  onSuccess: (challengeId: number) => void;
}

interface Friend {
  id: number;
  username: string;
  avatar?: string;
  isOnline: boolean;
}

const CreateChallengeModal: React.FC<CreateChallengeModalProps> = ({ game, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [stake, setStake] = useState<string>(game.minStake.toFixed(2));
  const [isOpenChallenge, setIsOpenChallenge] = useState<boolean>(false);
  const [selectedOpponent, setSelectedOpponent] = useState<number | null>(null);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [balance, setBalance] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFriends();
    fetchBalance();
  }, []);

  const fetchFriends = async () => {
    setLoadingFriends(true);
    try {
      // For now, we'll create a placeholder endpoint
      // In reality, this would call /api/friends/list
      const response = await apiService.get('/api/friends/list');
      if (response.success) {
        setFriends(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching friends:', err);
      // Set empty friends list for now
      setFriends([]);
    } finally {
      setLoadingFriends(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await apiService.get('/api/igaming-account/balance');
      if (response.success && response.data) {
        setBalance(response.data.balance || 0);
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
      setBalance(0);
    }
  };

  const validateStake = (): boolean => {
    const stakeAmount = parseFloat(stake);
    
    if (isNaN(stakeAmount) || stakeAmount <= 0) {
      setError('Please enter a valid stake amount');
      return false;
    }
    
    if (stakeAmount < game.minStake) {
      setError(`Minimum stake is €${game.minStake.toFixed(2)}`);
      return false;
    }
    
    if (stakeAmount > game.maxStake) {
      setError(`Maximum stake is €${game.maxStake.toFixed(2)}`);
      return false;
    }
    
    if (stakeAmount > balance) {
      setError('Insufficient balance in iGaming account');
      return false;
    }
    
    return true;
  };

  const handleCreateChallenge = async () => {
    setError(null);
    
    if (!validateStake()) {
      return;
    }
    
    if (!isOpenChallenge && !selectedOpponent) {
      setError('Please select an opponent or create an open challenge');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.post('/api/p2p-games/challenges/create', {
        gameId: game.id,
        opponentId: isOpenChallenge ? null : selectedOpponent,
        stake: parseFloat(stake),
        isOpenChallenge: isOpenChallenge
      });

      if (response.success) {
        toast.success('Challenge created successfully!');
        onSuccess(response.data.id);
      } else {
        toast.error(response.message || 'Failed to create challenge');
      }
    } catch (err: any) {
      console.error('Error creating challenge:', err);
      toast.error(err.message || 'Failed to create challenge');
    } finally {
      setLoading(false);
    }
  };

  const filteredFriends = friends.filter(friend =>
    friend.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const platformFee = parseFloat(stake || '0') * 0.05;
  const totalPrizePool = parseFloat(stake || '0') * 2;
  const potentialWinnings = totalPrizePool - platformFee;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          maxWidth: 500,
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a202c', marginBottom: 2 }}>
              Create Challenge
            </h2>
            <p style={{ fontSize: 13, color: '#718096' }}>
              {game.name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: 'none',
              background: '#f7fafc',
              color: '#4a5568',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18
            }}
          >
            <FaTimes />
          </button>
        </div>

        <div style={{ padding: '16px 20px 20px 20px' }}>
          {/* Balance Display */}
          <div style={{
            padding: 12,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 10,
            marginBottom: 16,
            color: 'white'
          }}>
            <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 4 }}>
              iGaming Account Balance
            </div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>
              €{balance.toFixed(2)}
            </div>
          </div>

          {/* Challenge Type Toggle */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#4a5568', marginBottom: 8, display: 'block' }}>
              Challenge Type
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button
                onClick={() => {
                  setIsOpenChallenge(false);
                  setSelectedOpponent(null);
                }}
                style={{
                  padding: '12px 16px',
                  background: !isOpenChallenge ? '#667eea' : 'white',
                  color: !isOpenChallenge ? 'white' : '#4a5568',
                  border: `2px solid ${!isOpenChallenge ? '#667eea' : '#e2e8f0'}`,
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'all 0.3s ease'
                }}
              >
                <FaUser style={{ fontSize: 24 }} />
                Challenge Friend
              </button>
              <button
                onClick={() => {
                  setIsOpenChallenge(true);
                  setSelectedOpponent(null);
                }}
                style={{
                  padding: '12px 16px',
                  background: isOpenChallenge ? '#48bb78' : 'white',
                  color: isOpenChallenge ? 'white' : '#4a5568',
                  border: `2px solid ${isOpenChallenge ? '#48bb78' : '#e2e8f0'}`,
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'all 0.3s ease'
                }}
              >
                <FaGlobe style={{ fontSize: 24 }} />
                Open Challenge
              </button>
            </div>
            <p style={{ fontSize: 12, color: '#718096', marginTop: 8 }}>
              {isOpenChallenge
                ? 'Anyone can accept your challenge'
                : 'Only your selected friend can accept'}
            </p>
          </div>

          {/* Friend Selection */}
          {!isOpenChallenge && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#4a5568', marginBottom: 8, display: 'block' }}>
                Select Opponent
              </label>
              
              {loadingFriends ? (
                <div style={{ textAlign: 'center', padding: 16, color: '#718096', fontSize: 13 }}>
                  Loading friends...
                </div>
              ) : friends.length === 0 ? (
                <div style={{
                  padding: 16,
                  background: '#f7fafc',
                  borderRadius: 8,
                  textAlign: 'center',
                  color: '#718096'
                }}>
                  <FaUsers style={{ fontSize: 32, marginBottom: 8 }} />
                  <p style={{ fontSize: 14, fontWeight: 600 }}>No friends available</p>
                  <p style={{ fontSize: 12, marginTop: 4, marginBottom: 12 }}>Add friends to challenge them!</p>
                  <button
                    onClick={() => {
                      onClose();
                      // Use setTimeout to ensure modal closes before navigation
                      setTimeout(() => {
                        navigate('/friends');
                      }, 100);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      marginTop: 8
                    }}
                  >
                    Manage Friends
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Search friends..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      fontSize: 14,
                      marginBottom: 12,
                      outline: 'none'
                    }}
                  />
                  <div style={{
                    maxHeight: 200,
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8
                  }}>
                    {filteredFriends.map(friend => (
                      <div
                        key={friend.id}
                        onClick={() => setSelectedOpponent(friend.id)}
                        style={{
                          padding: 12,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          background: selectedOpponent === friend.id ? '#f0f4ff' : 'white',
                          borderBottom: '1px solid #e2e8f0',
                          transition: 'background 0.2s ease'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: 16,
                            fontWeight: 700
                          }}>
                            {friend.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1a202c' }}>
                              {friend.username}
                            </div>
                            <div style={{ fontSize: 12, color: friend.isOnline ? '#48bb78' : '#a0aec0' }}>
                              {friend.isOnline ? '● Online' : '○ Offline'}
                            </div>
                          </div>
                        </div>
                        {selectedOpponent === friend.id && (
                          <div style={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: '#667eea',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12
                          }}>
                            ✓
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Stake Amount */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#4a5568', marginBottom: 8, display: 'block' }}>
              Stake Amount
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 16,
                fontWeight: 700,
                color: '#4a5568'
              }}>
                €
              </span>
              <input
                type="number"
                min={game.minStake}
                max={game.maxStake}
                step="0.01"
                value={stake}
                onChange={e => {
                  setStake(e.target.value);
                  setError(null);
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px 10px 32px',
                  border: '2px solid #e2e8f0',
                  borderRadius: 10,
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#1a202c',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 8,
              fontSize: 12,
              color: '#718096'
            }}>
              <span>Min: €{game.minStake.toFixed(2)}</span>
              <span>Max: €{game.maxStake.toFixed(2)}</span>
            </div>
            
            {/* Quick Stake Buttons */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {[game.minStake, game.minStake * 2, game.minStake * 5, game.minStake * 10].map(amount => (
                amount <= game.maxStake && (
                  <button
                    key={amount}
                    onClick={() => setStake(amount.toFixed(2))}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      background: '#f7fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#4a5568',
                      cursor: 'pointer'
                    }}
                  >
                    €{amount.toFixed(0)}
                  </button>
                )
              ))}
            </div>
          </div>

          {/* Prize Breakdown */}
          <div style={{
            padding: 12,
            background: '#f7fafc',
            borderRadius: 10,
            marginBottom: 16
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#4a5568', marginBottom: 8 }}>
              Prize Breakdown
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#718096' }}>Total Prize Pool:</span>
                <span style={{ fontWeight: 600, color: '#1a202c' }}>€{totalPrizePool.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#718096' }}>Platform Fee (5%):</span>
                <span style={{ fontWeight: 600, color: '#e53e3e' }}>-€{platformFee.toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 14,
                paddingTop: 6,
                borderTop: '1px solid #e2e8f0'
              }}>
                <span style={{ fontWeight: 700, color: '#48bb78' }}>Your Winnings:</span>
                <span style={{ fontWeight: 700, color: '#48bb78' }}>€{potentialWinnings.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: 10,
              background: '#fed7d7',
              border: '1px solid #fc8181',
              borderRadius: 8,
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: '#c53030'
            }}>
              <FaExclamationCircle style={{ fontSize: 14 }} />
              <span style={{ fontSize: 13 }}>{error}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '11px 16px',
                background: '#e2e8f0',
                color: '#4a5568',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateChallenge}
              disabled={loading}
              style={{
                flex: 2,
                padding: '11px 16px',
                background: loading ? '#cbd5e0' : '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: 16,
                    height: 16,
                    border: '2px solid white',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Creating...
                </>
              ) : (
                <>
                  <FaCoins />
                  Create Challenge
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CreateChallengeModal;

